<script lang="ts" setup>
    const { lang, country } = useLanguage();
    const { t, te, setLocale } = useI18n();
    const { commitCountryLanguage } = useCountryLanguageSwitch();
    const draftMode = useDraftMode();
    type FocusableElement = { focus: () => void };
    const closeButtonRef = ref<FocusableElement | null>(null);

    const modalId = HEADER_LANGUAGE_PICKER_MODAL_ID;

    const languagePickerModalController = getOrCreateModalController(modalId);
    let returnFocusElement: HTMLElement | null = null;

    const closeModal = () => {
        languagePickerModalController.close();
    };

    const { data: globalCountryConfigStory } = await useGlobalConfig<CountryConfigStoryblok>('globalcountryconfig', {});

    const rawCountryEntries = computed<CountryEntryStoryblok[]>(() => {
        return globalCountryConfigStory.value?.content?.CountryList || [];
    });

    const countriesList = computed<CountryListItem[]>(() => {
        return normalizeCountryEntries(rawCountryEntries.value).map((country) => {
            const key = `languagePicker.locations.${country.key}`;

            return {
                ...country,
                name: te(key) ? t(key) : country.name
            };
        });
    });

    const globalCountryCode = 'global';

    const globalCountryEntry = computed(() => {
        return countriesList.value.find((countryItem) => countryItem.key === globalCountryCode);
    });

    const countries = computed<CountryListItem[]>(() => {
        return countriesList.value.filter((countryItem) => countryItem.key !== globalCountryCode);
    });

    const pendingCountryCode = ref<string>(country.value);
    const pendingLanguageCode = ref<string>(lang.value);

    const setPendingCountry = (countryCode: string) => {
        pendingCountryCode.value = countryCode;

        const mode = draftMode.isDraftModeEnabled.value ? StoryblokVersion.Draft : StoryblokVersion.Published;
        const defaultLanguageValue = getDefaultLanguageForCountry(countryCode, mode);
        if (defaultLanguageValue) {
            pendingLanguageCode.value = defaultLanguageValue;
        }
    };

    const setPendingLanguage = (countryCode: string, languageCode: string) => {
        pendingCountryCode.value = countryCode;
        pendingLanguageCode.value = languageCode;
    };

    const selectCountryLanguage = async (countryCode: string, langCode: string) => {
        setPendingLanguage(countryCode, langCode);
    };

    const selectCountryDefaultLanguage = async (countryCode: string) => {
        setPendingCountry(countryCode);
    };

    const confirmSelection = async () => {
        if (!pendingCountryCode.value || !pendingLanguageCode.value) return;

        await commitCountryLanguage(pendingCountryCode.value, pendingLanguageCode.value);
        closeModal();
    };

    watch(lang, async (newLocale) => {
        // @ts-expect-error - setLocale expects either "de" or "en", but lang is typed as string
        if (newLocale) await setLocale(newLocale);
    });

    watch(
        () => languagePickerModalController.isOpen.value,
        async (isOpen) => {
            if (isOpen) {
                returnFocusElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
                pendingCountryCode.value = country.value;
                pendingLanguageCode.value = lang.value;

                await nextTick();
                closeButtonRef.value?.focus?.();
                return;
            }

            await nextTick();
            if (returnFocusElement?.isConnected) {
                returnFocusElement.focus();
            }
            returnFocusElement = null;
        },
        { flush: 'post' }
    );
</script>
<template>
    <ClientOnly>
        <SharedModal
            :controller="languagePickerModalController"
            :is-story-blok-modal="false"
            :no-padding="true"
            class="languages-modal"
            :modal-id="modalId"
        >
            <div class="content">
                <div class="header">
                    <h2>{{ $t('languagePicker.globalHeadline') }}</h2>
                    <SharedCloseButton
                        ref="closeButtonRef"
                        :label="$t('languagePicker.close')"
                        :icon-inverted="true"
                        @click="closeModal"
                    />
                </div>

                <div class="country-list --global">
                    <HeaderLanguagePickerCountryItem
                        v-if="globalCountryEntry"
                        :country="globalCountryEntry"
                        :is-global="true"
                        :pending-country-code="pendingCountryCode"
                        :pending-language-code="pendingLanguageCode"
                        @select-country-default-language="selectCountryDefaultLanguage"
                        @select-country-language="selectCountryLanguage"
                    />
                </div>

                <div class="header">
                    <h3>{{ $t('languagePicker.headline') }}</h3>
                </div>

                <div
                    class="country-list"
                    role="radiogroup"
                >
                    <HeaderLanguagePickerCountryItem
                        v-for="countryItem in countries"
                        :key="countryItem.key"
                        :country="countryItem"
                        :pending-country-code="pendingCountryCode"
                        :pending-language-code="pendingLanguageCode"
                        @select-country-default-language="selectCountryDefaultLanguage"
                        @select-country-language="selectCountryLanguage"
                    />
                </div>

                <div class="modal-actions">
                    <PrimaryButton
                        id="confirm-selection-button"
                        :button-text="$t('languagePicker.save')"
                        product-button
                        @click="confirmSelection"
                    />
                </div>
            </div>
        </SharedModal>

        <template #fallback>
            <span style="display: none" />
        </template>
    </ClientOnly>
</template>
<style scoped lang="scss">
    @import '~/assets/scss/navigation/header-language-picker.scss';
</style>
